import { Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';

import { accentClasses } from '../../../features/dashboard/widgets/accent';

@Component({
  selector: 'app-meter-tile',
  standalone: true,
  imports: [RouterLink],
  template: `
    <a
      [routerLink]="link()"
      class="card flex h-full w-full flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 transition-colors hover:border-primary-300 dark:border-surface-700/40 dark:bg-surface-900 dark:hover:border-primary-700"
    >
      <div class="flex items-center justify-between gap-3">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="text-[10px] font-bold uppercase tracking-wider text-text-muted">{{ label() }}</span>
          @if (loading()) {
          <span class="my-1 h-8 w-16 animate-pulse rounded bg-surface-200 dark:bg-surface-700"></span>
          } @else {
          <div class="flex items-baseline gap-1">
            <span class="text-3xl font-extrabold tracking-tight text-text-main">{{ value() }}</span>
            @if (max() != null) {
            <span class="text-sm font-medium text-text-muted">/ {{ max() }}</span>
            }
            @if (unit()) {
            <span class="text-sm font-medium text-text-muted">{{ unit() }}</span>
            }
          </div>
          }
        </div>
        @if (icon()) {
        <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" [class]="accentBox()">
          <i [class]="icon()" class="text-xl"></i>
        </div>
        }
      </div>

      @if (!loading() && max() != null) {
      <!-- Recessive track + single-hue fill; the bar carries magnitude, ink carries the number. -->
      <div
        class="h-2 w-full overflow-hidden rounded-full bg-surface-100 dark:bg-surface-800"
        role="meter"
        [attr.aria-valuenow]="value()"
        [attr.aria-valuemax]="max()"
      >
        <div
          class="h-full rounded-full bg-primary-500 transition-[width] duration-500 dark:bg-primary-400"
          [style.width.%]="pct()"
        ></div>
      </div>
      }
    </a>
  `,
})
export class MeterTileComponent {
  /** Current value (numerator). Pass '—' when unavailable. */
  value = input.required<string | number>();
  /** Limit (denominator) that the bar fills toward. */
  max = input<number | undefined>(undefined);
  label = input.required<string>();
  unit = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  link = input.required<string | any[]>();
  loading = input<boolean>(false);

  accentBox(): string {
    return accentClasses(this.accent());
  }

  /** Filled fraction, clamped to 0–100. */
  pct = computed(() => {
    const v = Number(this.value());
    const m = this.max();
    if (!m || !isFinite(v)) return 0;
    return Math.max(0, Math.min(100, (v / m) * 100));
  });
}
